// One line standing for a block of error, tool or system text, with a View button that opens the
// whole text in a dialog: scrollable, monospace and wrapped, with Copy (S131). Phrasing content
// only, so it can sit inside a paragraph such as a page header's description.
import { Check, Copy, Info, TerminalWindow, WarningCircle, X } from "@phosphor-icons/react";
import { useState } from "react";
import { Button, Modal } from "rizzui";

import { cn } from "./cn";
import { summaryLine, type OutputKind } from "./output";
import { useT } from "../i18n";

const ICONS = { error: WarningCircle, output: TerminalWindow, system: Info } as const;
const TONES = { error: "text-red-dark", output: "text-gray-600", system: "text-gray-600" } as const;

/** `brief`: no summary text below the `sm` breakpoint, only the kind and View (a narrow table cell). */
export function OutputPeek({ kind, text, title, brief, className }: { kind: OutputKind; text: string; title?: string; brief?: boolean; className?: string }) {
  const { common } = useT();
  const t = common.output;
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const Icon = ICONS[kind];
  const heading = title ?? t[kind];
  const copy = async () => {
    await navigator.clipboard?.writeText(text);
    setCopied(true);
  };
  return (
    <span className={cn("flex min-w-0 max-w-full items-center gap-1.5 text-xs", TONES[kind], className)}>
      <Icon weight="duotone" className="h-3.5 w-3.5 shrink-0" aria-hidden />
      <span className="shrink-0 font-medium">{t[kind]}:</span>
      <span className={cn("min-w-0 truncate", brief && "max-sm:hidden")} title={summaryLine(text, 400)}>
        {summaryLine(text)}
      </span>
      <button
        type="button"
        className="shrink-0 rounded px-1 font-medium text-primary underline-offset-2 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
        aria-label={`${t.view}: ${heading}`}
        onClick={() => {
          setCopied(false);
          setOpen(true);
        }}
      >
        {t.view}
      </button>
      <Modal isOpen={open} onClose={() => setOpen(false)} size="lg" containerClassName="dark:bg-gray-100 max-sm:!w-full">
        <div className="flex max-h-[85vh] flex-col p-5 sm:p-6" role="group" aria-label={heading}>
          <div className="mb-3 flex items-start gap-3">
            <Icon weight="duotone" className={cn("mt-0.5 h-5 w-5 shrink-0", TONES[kind])} aria-hidden />
            <div className="min-w-0 flex-1">
              <h2 className="m-0 break-words text-base">{heading}</h2>
              <p className="mb-0 mt-0.5 text-[13px] text-gray-500">{t.dialogHint}</p>
            </div>
            <button type="button" className="shrink-0 rounded p-1 text-gray-500 hover:text-gray-900" aria-label={common.actions.close} onClick={() => setOpen(false)}>
              <X className="h-4 w-4" />
            </button>
          </div>
          <pre
            data-testid="output-full"
            className="m-0 max-h-[70vh] min-h-0 flex-1 overflow-auto whitespace-pre-wrap rounded-lg bg-gray-1000 p-3 font-mono text-[12px] leading-5 text-gray-0 [overflow-wrap:anywhere] dark:bg-gray-50 dark:text-gray-900"
          >
            {text}
          </pre>
          <div className="mt-4 flex justify-end gap-2">
            <Button size="sm" variant="outline" className="gap-1" onClick={() => void copy()}>
              {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />} {copied ? t.copied : common.actions.copy}
            </Button>
            <Button size="sm" onClick={() => setOpen(false)}>
              {common.actions.close}
            </Button>
          </div>
        </div>
      </Modal>
    </span>
  );
}
